import React from 'react';

/**
 * Error category types for better classification and handling
 */
export enum ErrorCategory {
  NETWORK = 'network',
  RUNTIME = 'runtime',
  RENDER = 'render',
  API = 'api',
  UNKNOWN = 'unknown',
}

/**
 * Error metadata interface for analytics and logging
 */
interface ErrorMetadata {
  category: ErrorCategory;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: number;
  url: string;
  userAgent: string;
  componentStack?: string;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallbackComponent?: React.ComponentType<ErrorFallbackProps>;
  onError?: (error: Error, errorInfo: React.ErrorInfo, metadata: ErrorMetadata) => void;
  enableRetry?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorMetadata?: ErrorMetadata;
  retryCount: number;
}

export interface ErrorFallbackProps {
  error?: Error;
  errorMetadata?: ErrorMetadata;
  resetError: () => void;
  retryCount: number;
}

/**
 * Categorize errors based on type and message patterns
 */
function categorizeError(error: Error): ErrorCategory {
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  // Network errors
  if (
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('timeout') ||
    name.includes('networkerror')
  ) {
    return ErrorCategory.NETWORK;
  }

  // API errors
  if (
    message.includes('api') ||
    message.includes('response') ||
    message.includes('status')
  ) {
    return ErrorCategory.API;
  }

  // Render errors
  if (
    message.includes('render') ||
    message.includes('component') ||
    name.includes('invariant')
  ) {
    return ErrorCategory.RENDER;
  }

  // Runtime errors
  if (
    name.includes('typeerror') ||
    name.includes('referenceerror') ||
    name.includes('rangeerror')
  ) {
    return ErrorCategory.RUNTIME;
  }

  return ErrorCategory.UNKNOWN;
}

/**
 * Determine error severity based on category and context
 */
function determineErrorSeverity(
  category: ErrorCategory,
  error: Error
): 'low' | 'medium' | 'high' | 'critical' {
  // Critical: Errors that break core functionality
  if (category === ErrorCategory.RENDER || error.message.includes('critical')) {
    return 'critical';
  }

  // High: Network and API errors that affect user experience
  if (category === ErrorCategory.NETWORK || category === ErrorCategory.API) {
    return 'high';
  }

  // Medium: Runtime errors that may be recoverable
  if (category === ErrorCategory.RUNTIME) {
    return 'medium';
  }

  // Low: Unknown errors or less impactful issues
  return 'low';
}

/**
 * Send error data to analytics endpoint
 */
function logErrorToAnalytics(
  error: Error,
  errorInfo: React.ErrorInfo,
  metadata: ErrorMetadata
): void {
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.group('🔴 ErrorBoundary caught an error');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Metadata:', metadata);
    console.groupEnd();
  }

  // Send to analytics in production
  if (process.env.NODE_ENV === 'production') {
    try {
      // Send to your analytics endpoint
      const analyticsData = {
        type: 'error',
        category: metadata.category,
        severity: metadata.severity,
        message: error.message,
        stack: error.stack,
        componentStack: metadata.componentStack,
        timestamp: metadata.timestamp,
        url: metadata.url,
        userAgent: metadata.userAgent,
      };

      // Option 1: Send to custom analytics endpoint
      fetch('/api/analytics/error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(analyticsData),
        keepalive: true,
      }).catch(() => {
        // Silently fail if analytics endpoint is unavailable
      });

      // Option 2: Send to Google Analytics 4 (if configured)
      if (window.gtag) {
        window.gtag('event', 'exception', {
          description: error.message,
          fatal: metadata.severity === 'critical',
          category: metadata.category,
        });
      }
    } catch (analyticsError) {
      // Don't let analytics errors break the error boundary
      console.error('Failed to log error to analytics:', analyticsError);
    }
  }
}

/**
 * Default fallback component with improved UI
 */
const DefaultErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  errorMetadata,
  resetError,
  retryCount,
}) => {
  const maxRetries = 3;
  const canRetry = retryCount < maxRetries;

  // Get user-friendly error message
  const getUserMessage = () => {
    if (!errorMetadata) return 'An unexpected error occurred.';

    switch (errorMetadata.category) {
      case ErrorCategory.NETWORK:
        return 'Network connection issue detected. Please check your internet connection.';
      case ErrorCategory.API:
        return 'Unable to communicate with the server. Please try again.';
      case ErrorCategory.RENDER:
        return 'A component failed to render properly.';
      case ErrorCategory.RUNTIME:
        return 'A technical error occurred while processing your request.';
      default:
        return 'An unexpected error occurred.';
    }
  };

  // Get severity-based styling
  const getSeverityStyles = () => {
    if (!errorMetadata) return 'bg-red-50 border-red-200 text-red-800';

    switch (errorMetadata.severity) {
      case 'critical':
        return 'bg-red-100 border-red-300 text-red-900';
      case 'high':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'medium':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'low':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-red-50 border-red-200 text-red-800';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className={`max-w-2xl w-full p-8 border-2 rounded-xl shadow-lg ${getSeverityStyles()}`}>
        {/* Error Icon */}
        <div className="flex items-center mb-4">
          <svg
            className="w-8 h-8 mr-3"
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <h2 className="text-2xl font-bold">Something Went Wrong</h2>
        </div>

        {/* User-friendly message */}
        <p className="text-base mb-4">{getUserMessage()}</p>

        {/* Error details (development only) */}
        {process.env.NODE_ENV === 'development' && error && (
          <details className="mb-4 p-4 bg-white bg-opacity-50 rounded-lg">
            <summary className="cursor-pointer font-semibold mb-2">
              Technical Details (Dev Only)
            </summary>
            <div className="text-sm font-mono space-y-2">
              <div>
                <strong>Error:</strong> {error.message}
              </div>
              {errorMetadata && (
                <>
                  <div>
                    <strong>Category:</strong> {errorMetadata.category}
                  </div>
                  <div>
                    <strong>Severity:</strong> {errorMetadata.severity}
                  </div>
                  <div>
                    <strong>Timestamp:</strong> {new Date(errorMetadata.timestamp).toISOString()}
                  </div>
                </>
              )}
              {error.stack && (
                <div>
                  <strong>Stack:</strong>
                  <pre className="mt-1 p-2 bg-gray-800 text-gray-100 rounded overflow-x-auto text-xs">
                    {error.stack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Retry button */}
          {canRetry && (
            <button
              onClick={resetError}
              className="flex-1 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg 
                       hover:bg-blue-700 transition-colors duration-200 shadow-md
                       focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Try Again {retryCount > 0 && `(${maxRetries - retryCount} attempts left)`}
            </button>
          )}

          {/* Reload page button */}
          <button
            onClick={() => window.location.reload()}
            className="flex-1 px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg 
                     hover:bg-gray-700 transition-colors duration-200 shadow-md
                     focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Reload Page
          </button>

          {/* Go home button */}
          <button
            onClick={() => (window.location.href = '/')}
            className="flex-1 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg 
                     hover:bg-green-700 transition-colors duration-200 shadow-md
                     focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Go Home
          </button>
        </div>

        {/* Retry count warning */}
        {!canRetry && (
          <p className="mt-4 text-sm font-semibold text-center">
            Maximum retry attempts reached. Please reload the page or contact support.
          </p>
        )}

        {/* Support information */}
        <div className="mt-6 pt-6 border-t border-current border-opacity-20">
          <p className="text-sm text-center">
            If the problem persists, please contact support with error code:{' '}
            <code className="font-mono bg-white bg-opacity-50 px-2 py-1 rounded">
              {errorMetadata?.timestamp || Date.now()}
            </code>
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Enhanced ErrorBoundary with analytics, retry, and categorization
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Create error metadata
    const metadata: ErrorMetadata = {
      category: categorizeError(error),
      severity: determineErrorSeverity(categorizeError(error), error),
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
      componentStack: errorInfo.componentStack || undefined,
    };

    // Update state with metadata
    this.setState({
      errorInfo,
      errorMetadata: metadata,
    });

    // Log to analytics
    logErrorToAnalytics(error, errorInfo, metadata);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo, metadata);
    }
  }

  resetError = (): void => {
    this.setState((prevState) => ({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorMetadata: undefined,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallbackComponent || DefaultErrorFallback;

      return (
        <FallbackComponent
          error={this.state.error}
          errorMetadata={this.state.errorMetadata}
          resetError={this.resetError}
          retryCount={this.state.retryCount}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
